import { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn, formatTime } from '@/lib/utils';
import { Truck, Phone, Mail, Moon, Navigation, ParkingSquare, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { t } from '@/i18n';
import { getCarrier } from '@/data/arrivalsData';
import { stateStyles, stateLabel, channelIcons, channelLabel } from './arrivalsUi';
import { AnnouncePreview } from './AnnouncePreview';
import { ReplySimulator } from './ReplySimulator';
import { VoiceCall } from './VoiceCall';
import { DocumentReader } from './DocumentReader';
import { ReplanProposal, REPLAN_STATES } from './ReplanProposal';
import { RiskBadge, riskReasons } from './RiskBadge';
import { noShowRisk } from '@/lib/noShowRisk';
import { HelpTip } from '@/components/ui/help-tip';

const Row = ({ label, children }) => (
  <div className="flex justify-between gap-3 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium text-right">{children}</span>
  </div>
);

export const LoadingDetailSheet = ({ loading, loadings = [], onClose, onAnnounced, onReply, onReplan, onDocument }) => {
  const [announceOpen, setAnnounceOpen] = useState(false);
  if (!loading) return null;
  const carrier = getCarrier(loading.carrierId);
  const tomorrow = loading.eta && new Date(loading.eta).getDate() !== new Date().getDate();
  const risk = noShowRisk(loading);

  return (
    <Sheet open={!!loading} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Truck className="h-6 w-6 text-primary" />
            </div>
            <div>
              <SheetTitle className="text-xl">{loading.plate}</SheetTitle>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Badge variant="outline" className={stateStyles[loading.state]}>{stateLabel(loading.state)}</Badge>
                <Badge variant="outline">{loading.gate}</Badge>
                {loading.storyId && <span className="text-xs text-muted-foreground">{loading.storyId}</span>}
              </div>
            </div>
          </div>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {onReplan && REPLAN_STATES.includes(loading.state) && (
            <ReplanProposal key={loading.id} loading={loading} loadings={loadings} onAccept={onReplan} />
          )}

          {/* Carrier & dispatcher */}
          <div className="bg-muted/50 rounded-xl p-4 space-y-2">
            <div className="text-xs font-semibold text-muted-foreground uppercase">{t('arrivals.detail.carrier')}</div>
            <div className="font-semibold">{carrier.name}</div>
            <div className="text-sm text-muted-foreground">
              {carrier.dispatcher.name} · {t('arrivals.detail.prefers')} {channelLabel(carrier.channel)} · {carrier.lang.toUpperCase()}
            </div>
            <div className="flex gap-2 flex-wrap pt-1">
              <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={() => toast.success(`${t('truckDetail.calling')} ${carrier.dispatcher.phone}`)}>
                <Phone className="h-3 w-3" /> {carrier.dispatcher.phone}
              </Button>
              <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={() => toast.success(t('arrivals.detail.emailDemo'))}>
                <Mail className="h-3 w-3" /> {carrier.dispatcher.email}
              </Button>
              <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={() => setAnnounceOpen(true)}>
                <Link2 className="h-3 w-3" /> {t('announce.button')}
              </Button>
              <HelpTip id="announce" className="self-center" />
            </div>
          </div>

          {/* Driver */}
          <div className="bg-muted/50 rounded-xl p-4 space-y-2">
            <div className="text-xs font-semibold text-muted-foreground uppercase">{t('arrivals.detail.driver')}</div>
            <Row label={t('arrivals.detail.name')}>{loading.driver.name}</Row>
            <Row label={t('arrivals.detail.power')}>
              {loading.power
                ? <span className="flex items-center gap-1 text-orange-700"><Moon className="h-3 w-3" />{t('arrivals.powerFrom')} {loading.power}</span>
                : t('arrivals.detail.powerUnknown')}
            </Row>
            {loading.gpsKm != null && (
              <Row label="GPS">
                <span className="flex items-center gap-1 text-sky-700"><Navigation className="h-3 w-3" />{loading.gpsKm} km {t('arrivals.detail.fromPlant')}</span>
              </Row>
            )}
            {loading.parking && (
              <Row label={t('arrivals.detail.parking')}>
                <span className="flex items-center gap-1"><ParkingSquare className="h-3 w-3" />{t('arrivals.detail.parkingJesenik')}</span>
              </Row>
            )}
          </div>

          {/* Loading */}
          <div className="bg-muted/50 rounded-xl p-4 space-y-2">
            <div className="text-xs font-semibold text-muted-foreground uppercase">{t('arrivals.detail.loading')}</div>
            <Row label={t('arrivals.detail.order')}>{loading.orderId}</Row>
            <Row label={t('arrivals.detail.customer')}>{loading.customer}</Row>
            <Row label={t('arrivals.detail.material')}>{loading.material} · {loading.weight} t</Row>
            <Row label={t('arrivals.detail.type')}>{t(`arrivals.type.${loading.type}`)} · {loading.gate}</Row>
            <Row label={t('arrivals.plan')}>{formatTime(loading.plannedAt)}</Row>
            {risk && (
              <div className="pt-1 space-y-1">
                <Row label={<span className="inline-flex items-center gap-1">{t('ai.risk.label')} <HelpTip id="risk" /></span>}><RiskBadge risk={risk} /></Row>
                <ul className="text-[11px] text-muted-foreground list-disc pl-4">{riskReasons(risk).map(r => <li key={r}>{r}</li>)}</ul>
                <div className="text-[10px] text-muted-foreground italic">{t('ai.risk.note')}</div>
              </div>
            )}
            <Row label={t('arrivals.eta')}>
              {loading.eta ? `${formatTime(loading.eta)}${tomorrow ? ` (${t('arrivals.tomorrow')})` : ''}` : '—'}
            </Row>
          </div>

          {/* Communication */}
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">{t('arrivals.detail.communication')}</div>
            {loading.messages.length === 0 ? (
              <div className="text-sm text-muted-foreground bg-muted/50 rounded-xl p-4 text-center">{t('arrivals.detail.noMessages')}</div>
            ) : (
              <div className="space-y-2">
                {loading.messages.map((m, i) => {
                  const Icon = channelIcons[m.channel] || Truck;
                  return (
                    <div key={i} className={cn('flex', m.dir === 'out' ? 'justify-end' : 'justify-start')}>
                      <div className={cn(
                        'max-w-[85%] rounded-xl px-3 py-2 text-sm',
                        m.dir === 'out' ? 'bg-primary text-white rounded-br-sm'
                          : m.dir === 'system' ? 'bg-muted text-muted-foreground'
                            : 'bg-emerald-50 border border-emerald-200 rounded-bl-sm',
                      )}>
                        <div>{m.text}</div>
                        {m.textCs && <div className={cn('text-xs italic mt-1', m.dir === 'out' ? 'text-white/70' : 'text-muted-foreground')}>{m.textCs}</div>}
                        <div className={cn('text-[10px] mt-1 flex items-center gap-1', m.dir === 'out' ? 'text-white/70' : 'text-muted-foreground')}>
                          <Icon className="h-2.5 w-2.5" /> {channelLabel(m.channel)} · {formatTime(m.time)}
                          {m.auto && ` · ${t('arrivals.detail.auto')}`}
                          {m.ai && ` · ${t('ai.reply.readByAi')}`}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {onReply && <VoiceCall key={`call-${loading.id}`} loading={loading} carrier={carrier} onResult={onReply} />}

          {onDocument && <DocumentReader key={`doc-${loading.id}`} loading={loading} carrier={carrier} onAttach={onDocument} />}

          {onReply && <ReplySimulator key={loading.id} loading={loading} carrier={carrier} onApply={onReply} />}
        </div>
      </SheetContent>
      <AnnouncePreview
        carrier={carrier}
        loadings={loadings}
        open={announceOpen}
        onOpenChange={setAnnounceOpen}
        onSaved={onAnnounced}
      />
    </Sheet>
  );
};
